// Countdown-only adapter for the two October 2026 merchandising drops.
// Wait for the native urgency controller's first completed render before replacing
// that controller, so its initial request cannot recreate a second clock.
(() => {
  if (Date.now() >= Date.parse('2026-11-01T00:00:00+02:00')) return;
  if (!document.getElementById('nutpmx-badges-config')) return;
  let started = false;
  let observer = null;
  const boot = () => {
/* Campaign urgency merchandising — header countdown + eligible PDP offer card. */
(() => {
  const previousController = window.__nutpmxCountdownController;
  if (previousController && typeof previousController.destroy === "function") {
    previousController.destroy();
  }

  const configNode = document.getElementById("nutpmx-badges-config");
  if (!configNode) return;

  const proxyPath = configNode.dataset.nutpmxProxyPath || "/apps/nut-promo";
  const locale = String(
    configNode.dataset.nutpmxStoreLocale || document.documentElement.lang || "en",
  ).toLowerCase();
  const isArabic = locale === "ar" || locale.startsWith("ar-");
  const refreshSeconds = Math.max(
    15,
    Number.parseInt(configNode.dataset.nutpmxRefreshSeconds || "30", 10) || 30,
  );

  let settings = null;
  let campaigns = [];
  let bar = null;
  let amountNodes = [];
  let lastValues = [];
  let pdpCard = null;
  let pdpAmountNodes = [];
  let pdpLastValues = [];
  let pdpSignature = "";
  let activePdpCampaignCache = null;
  let lastKnownPdpVariantId = null;
  let pdpRecheckTimer = null;
  let serverOffsetMs = 0;
  let tickTimer = null;
  let refreshTimer = null;
  let visibilityTimer = null;
  let destroyed = false;

  const currentTime = () => Date.now() + serverOffsetMs;

  // October 2026 drops change the displayed countdown only. The B1G1 campaign,
  // product eligibility, gift assistant and native discount keep their full schedule.
  // Explicit Cairo offsets account for the daylight-saving change during Drop 2.
  // BEGIN OCTOBER DROP SCHEDULE
  const octoberDropCountdown = (campaignId, campaignEnd, now) => {
    const unchanged = { endsAt: campaignEnd, drop: null };
    const startsAt = Date.parse("2026-10-20T00:00:00+03:00");
    const dropOneEnd = Date.parse("2026-10-24T00:00:00+03:00");
    const dropTwoEnd = Date.parse("2026-11-01T00:00:00+02:00");
    if (
      campaignId !== "cmv4ym1ug001gpg3cgsoab3uy" ||
      !Number.isFinite(campaignEnd) ||
      now < startsAt ||
      now >= dropTwoEnd
    ) return unchanged;
    const drop = now < dropOneEnd ? 1 : 2;
    return {
      endsAt: Math.min(campaignEnd, drop === 1 ? dropOneEnd : dropTwoEnd),
      drop,
    };
  };
  // END OCTOBER DROP SCHEDULE

  const markCountdownDrop = (element, headingSelector, countdown) => {
    if (!element || !countdown.drop) return;
    element.dataset.nutpmxDrop = String(countdown.drop);
    element.dataset.nutpmxDropEndsAt = new Date(countdown.endsAt).toISOString();
    const heading = element.querySelector(headingSelector);
    const text = isArabic
      ? (countdown.drop === 1 ? "تنتهي المرحلة الأولى خلال" : "تنتهي المرحلة الثانية خلال")
      : `DROP ${countdown.drop} ENDS IN`;
    if (heading && heading.textContent !== text) heading.textContent = text;
  };

  const parseTime = (value) => {
    if (!value) return null;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const normalizeVariantId = (value) => {
    if (value === null || value === undefined) return null;
    const match = String(value).match(/(\d+)(?:\D*)$/);
    return match ? match[1] : null;
  };

  const pickText = (english, arabic, englishFallback, arabicFallback) => {
    const primary = isArabic ? arabic : english;
    const secondary = isArabic ? english : arabic;
    return String(
      primary || secondary || (isArabic ? arabicFallback : englishFallback) || "",
    ).trim();
  };

  const makeElement = (tag, className, text = "") => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  };

  const currentProductHandle = () => {
    const fromLiquid = String(configNode.dataset.nutpmxProductHandle || "").trim();
    if (fromLiquid) return fromLiquid.toLowerCase();

    const match = window.location.pathname.match(/\/products\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]).toLowerCase() : null;
  };

  const primaryPdpProductForm = () => {
    const picker = document.querySelector(
      ".nut-pdp-main hdt-variant-picker[form], .nut-pdp-section hdt-variant-picker[form]",
    );
    const formId = picker instanceof HTMLElement ? String(picker.getAttribute("form") || "") : "";
    if (formId) {
      const form = document.getElementById(formId);
      if (form instanceof HTMLFormElement) return form;
    }

    const forms = Array.from(
      document.querySelectorAll(
        ".nut-pdp-main form[action*='/cart/add'], .nut-pdp-section form[action*='/cart/add']",
      ),
    );
    return forms.find((form) =>
      form instanceof HTMLFormElement &&
      !form.closest(".hdt-sticky-btn-atc, [data-quick-view], .hdt-quick-view") &&
      form.querySelector("button[name='add'], .hdt-product-form__submit")
    ) || null;
  };

  const currentProductVariantId = () => {
    try {
      const fromUrl = normalizeVariantId(new URL(window.location.href).searchParams.get("variant"));
      if (fromUrl) {
        lastKnownPdpVariantId = fromUrl;
        return fromUrl;
      }
    } catch {
      // Fall through to the live product form.
    }

    // Read only the canonical PDP form. The NUT theme also renders sticky ATC
    // and other forms with name=id; sampling all of them can oscillate between
    // different variant ids while the theme updates its UI.
    const form = primaryPdpProductForm();
    const canonical = form?.querySelector("[name='id']");
    if (canonical instanceof HTMLInputElement || canonical instanceof HTMLSelectElement) {
      const normalized = normalizeVariantId(canonical.value);
      if (normalized) {
        lastKnownPdpVariantId = normalized;
        return normalized;
      }
    }

    if (lastKnownPdpVariantId) return lastKnownPdpVariantId;

    const fromLiquid = normalizeVariantId(configNode.dataset.nutpmxProductVariantId);
    if (fromLiquid) {
      lastKnownPdpVariantId = fromLiquid;
      return fromLiquid;
    }

    const analyticsVariant = normalizeVariantId(
      window.ShopifyAnalytics?.meta?.selectedVariantId ||
      window.ShopifyAnalytics?.meta?.product?.variants?.[0]?.id,
    );
    if (analyticsVariant) lastKnownPdpVariantId = analyticsVariant;
    return analyticsVariant || null;
  };

  const updateAmounts = (nodes, valuesCache, remainingMs) => {
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    const values = [
      Math.floor(totalSeconds / 86400),
      Math.floor((totalSeconds % 86400) / 3600),
      Math.floor((totalSeconds % 3600) / 60),
      totalSeconds % 60,
    ];

    values.forEach((value, index) => {
      const node = nodes[index];
      if (!node) return;
      const next = String(value).padStart(2, "0");
      if (valuesCache[index] !== next) {
        node.textContent = next;
        if (valuesCache[index] !== undefined) {
          node.classList.remove("nutpmx-cdb-value-change");
          void node.offsetWidth;
          node.classList.add("nutpmx-cdb-value-change");
        }
        valuesCache[index] = next;
      }
    });
  };

  const getHeaderAnchor = () => {
    const customMarquee = document.querySelector(
      "[class^='ai-marquee-announcement-'], [class*=' ai-marquee-announcement-']",
    );
    if (customMarquee instanceof HTMLElement) {
      const section = customMarquee.closest(".shopify-section");
      return { element: section instanceof HTMLElement ? section : customMarquee, mode: "after" };
    }

    const announcement = document.querySelector(
      ".hdt-section-announcement-bar, #shopify-section-announcement-bar",
    );
    if (announcement instanceof HTMLElement) {
      return { element: announcement, mode: "after" };
    }

    const header = document.querySelector(
      ".hdt-section-header, #hdt-header, [class*='hdt-section-header']",
    );
    if (header instanceof HTMLElement) {
      return { element: header, mode: "before" };
    }

    return { element: document.body, mode: "prepend" };
  };

  const placeBar = () => {
    if (!bar || destroyed) return;
    const anchor = getHeaderAnchor();
    if (anchor.mode === "after") {
      if (anchor.element.nextElementSibling !== bar) {
        anchor.element.insertAdjacentElement("afterend", bar);
      }
      return;
    }
    if (anchor.mode === "before") {
      if (anchor.element.previousElementSibling !== bar) {
        anchor.element.parentElement?.insertBefore(bar, anchor.element);
      }
      return;
    }
    if (document.body.firstElementChild !== bar) document.body.prepend(bar);
  };

  const buildBar = () => {
    if (bar) bar.remove();
    amountNodes = [];
    lastValues = [];

    bar = makeElement("div", "nutpmx-cdb-shell");
    bar.dataset.nutpmxCountdownBar = "true";
    bar.setAttribute("aria-hidden", "true");

    const content = makeElement("section", "nutpmx-cdb");
    content.dir = isArabic ? "rtl" : "ltr";
    content.setAttribute("role", "region");
    content.setAttribute(
      "aria-label",
      isArabic ? "عرض محدود بوقت" : "Limited-time promotion",
    );

    const copy = makeElement("div", "nutpmx-cdb__copy");
    const message = makeElement(
      "strong",
      "nutpmx-cdb__message",
      pickText(
        settings?.messageEn,
        settings?.messageAr,
        "YOUR FREE PRODUCTS ARE WAITING",
        "منتجاتك المجانية في انتظارك",
      ),
    );
    const subheadline = makeElement(
      "span",
      "nutpmx-cdb__subheadline",
      pickText(
        settings?.subheadlineEn,
        settings?.subheadlineAr,
        "BUY MORE, GET MORE",
        "اشترِ أكثر، واحصل على أكثر",
      ),
    );
    copy.append(message, subheadline);
    content.appendChild(copy);

    const timerWrap = makeElement("div", "nutpmx-cdb__timer-wrap");
    const timerHeading = makeElement(
      "span",
      "nutpmx-cdb__timer-heading",
      isArabic ? "ينتهي العرض خلال" : "OFFER ENDS IN",
    );
    const timer = makeElement("div", "nutpmx-cdb__timer");
    timer.setAttribute("aria-live", "off");
    const labels = isArabic
      ? ["يوم", "ساعة", "دقيقة", "ثانية"]
      : ["DAYS", "HRS", "MIN", "SEC"];

    labels.forEach((label, index) => {
      const unit = makeElement("div", "nutpmx-cdb__unit");
      const amount = makeElement("strong", "nutpmx-cdb__amount", "00");
      amount.dataset.nutpmxCountdownIndex = String(index);
      const caption = makeElement("span", "nutpmx-cdb__label", label);
      unit.append(amount, caption);
      timer.appendChild(unit);
      amountNodes.push(amount);
    });
    timerWrap.append(timerHeading, timer);
    content.appendChild(timerWrap);

    const button = makeElement(
      "a",
      "nutpmx-cdb__button",
      pickText(settings?.buttonTextEn, settings?.buttonTextAr, "SHOP NOW", "تسوق الآن"),
    );
    button.href = String(settings?.buttonUrl || "/collections/all");
    const arrow = makeElement("span", "nutpmx-cdb__arrow", "→");
    arrow.setAttribute("aria-hidden", "true");
    button.appendChild(arrow);
    content.appendChild(button);

    bar.appendChild(content);
    placeBar();
  };

  const setBarVisible = (visible) => {
    if (!bar) return;
    if (visible) {
      bar.setAttribute("aria-hidden", "false");
      if (!bar.classList.contains("nutpmx-cdb-state-visible")) {
        window.requestAnimationFrame(() => {
          if (!destroyed && bar) bar.classList.add("nutpmx-cdb-state-visible");
        });
      }
    } else {
      bar.setAttribute("aria-hidden", "true");
      bar.classList.remove("nutpmx-cdb-state-visible");
    }
  };

  const getPdpAnchor = () => {
    // Prefer the actual buy-buttons block from the NUT PDP. This avoids
    // accidentally anchoring to sticky ATC, bundles, recommendations or quick view.
    const buyAreas = Array.from(
      document.querySelectorAll(
        ".nut-pdp-main hdt-buy-buttons, .nut-pdp-main .hdt-product-form__buttons, main hdt-buy-buttons",
      ),
    );
    for (const buyArea of buyAreas) {
      if (!(buyArea instanceof HTMLElement)) continue;
      if (buyArea.closest(".hdt-sticky-btn-atc, [data-quick-view], .hdt-quick-view")) continue;
      const form = buyArea.closest("form");
      if (!(form instanceof HTMLFormElement)) continue;
      if (!form.querySelector("button[name='add'], .hdt-product-form__submit")) continue;
      const wrapper = form.closest("wrapp-hdt-pr-frm");
      return wrapper instanceof HTMLElement ? wrapper : form;
    }

    const candidates = Array.from(
      document.querySelectorAll(
        ".nut-pdp-main wrapp-hdt-pr-frm, .nut-pdp-main form.hdt-product-form, main wrapp-hdt-pr-frm, main form.hdt-product-form",
      ),
    );
    for (const candidate of candidates) {
      if (!(candidate instanceof HTMLElement)) continue;
      if (candidate.closest(".hdt-sticky-btn-atc, [data-quick-view], .hdt-quick-view")) continue;
      const form = candidate.matches("form") ? candidate : candidate.querySelector("form.hdt-product-form");
      if (!(form instanceof HTMLFormElement)) continue;
      if (!form.querySelector("button[name='add'], .hdt-product-form__submit")) continue;
      return candidate.matches("wrapp-hdt-pr-frm")
        ? candidate
        : candidate.closest("wrapp-hdt-pr-frm") || candidate;
    }
    return null;
  };

  const placePdpCard = () => {
    if (!pdpCard || destroyed) return false;

    // Once mounted, leave the card in place. The NUT PDP can inject dynamic
    // nodes around the purchase form; forcing nextElementSibling every second
    // caused the same card to be repeatedly moved and visually blink.
    if (pdpCard.isConnected) return true;

    const anchor = getPdpAnchor();
    if (!(anchor instanceof HTMLElement)) return false;
    anchor.insertAdjacentElement("afterend", pdpCard);
    return pdpCard.isConnected;
  };

  const productRuleForCampaign = (campaign, handle) => {
    if (!Array.isArray(campaign?.products)) return null;
    return campaign.products.find(
      (product) => String(product?.handle || "").toLowerCase() === handle,
    ) || null;
  };

  const activePdpCampaign = (now) => {
    const handle = currentProductHandle();
    if (!handle) return null;
    const variantId = currentProductVariantId();

    for (const campaign of campaigns) {
      if (campaign?.pdpOffer?.enabled !== true) continue;
      const startsAt = parseTime(campaign.startsAt);
      const endsAt = parseTime(campaign.endsAt);
      if (startsAt !== null && now < startsAt) continue;
      if (endsAt === null || now >= endsAt) continue;

      const rule = productRuleForCampaign(campaign, handle);
      if (!rule) continue;
      const variantIds = Array.isArray(rule.variantIds)
        ? rule.variantIds.map(normalizeVariantId).filter(Boolean)
        : [];
      if (variantIds.length && variantId && !variantIds.includes(variantId)) continue;
      return campaign;
    }
    return null;
  };

  const buildPdpCard = (campaign) => {
    if (pdpCard) pdpCard.remove();
    pdpAmountNodes = [];
    pdpLastValues = [];

    const offer = campaign?.pdpOffer || {};
    pdpCard = makeElement("section", "nutpmx-pdp-offer");
    pdpCard.dataset.nutpmxPdpOffer = "true";
    pdpCard.dataset.nutpmxPdpCampaign = String(campaign?.id || "");
    pdpCard.dir = isArabic ? "rtl" : "ltr";
    pdpCard.setAttribute("aria-hidden", "true");
    pdpCard.setAttribute("role", "region");
    pdpCard.setAttribute(
      "aria-label",
      isArabic ? "تفاصيل العرض المحدود" : "Limited-time product offer",
    );

    const glow = makeElement("span", "nutpmx-pdp-offer__glow");
    glow.setAttribute("aria-hidden", "true");
    pdpCard.appendChild(glow);

    const eyebrow = makeElement(
      "span",
      "nutpmx-pdp-offer__eyebrow",
      isArabic ? "عرض لفترة محدودة" : "LIMITED-TIME OFFER",
    );
    const headline = makeElement(
      "strong",
      "nutpmx-pdp-offer__headline",
      pickText(
        offer.headlineEn,
        offer.headlineAr,
        "YOUR FREE PRODUCTS ARE WAITING",
        "منتجاتك المجانية في انتظارك",
      ),
    );
    const message = makeElement(
      "span",
      "nutpmx-pdp-offer__message",
      pickText(
        offer.messageEn,
        offer.messageAr,
        "BUY MORE, GET MORE",
        "اشترِ أكثر، واحصل على أكثر",
      ),
    );

    const timerArea = makeElement("div", "nutpmx-pdp-offer__timer-area");
    const timerHeading = makeElement(
      "span",
      "nutpmx-pdp-offer__timer-heading",
      isArabic ? "ينتهي خلال" : "ENDS IN",
    );
    const timer = makeElement("div", "nutpmx-pdp-offer__timer");
    const labels = isArabic
      ? ["يوم", "ساعة", "دقيقة", "ثانية"]
      : ["DAYS", "HRS", "MIN", "SEC"];
    labels.forEach((label, index) => {
      const unit = makeElement("span", "nutpmx-pdp-offer__unit");
      const amount = makeElement("strong", "nutpmx-pdp-offer__amount", "00");
      amount.dataset.nutpmxPdpCountdownIndex = String(index);
      const caption = makeElement("small", "nutpmx-pdp-offer__label", label);
      unit.append(amount, caption);
      timer.appendChild(unit);
      pdpAmountNodes.push(amount);
    });
    timerArea.append(timerHeading, timer);

    const button = makeElement(
      "a",
      "nutpmx-pdp-offer__button",
      pickText(
        offer.buttonTextEn,
        offer.buttonTextAr,
        "SHOP THE OFFER",
        "تسوق العرض",
      ),
    );
    button.href = String(offer.buttonUrl || "/collections/all");
    const arrow = makeElement("span", "nutpmx-pdp-offer__arrow", "→");
    arrow.setAttribute("aria-hidden", "true");
    button.appendChild(arrow);

    const footer = makeElement("div", "nutpmx-pdp-offer__footer");
    footer.append(timerArea, button);
    pdpCard.append(eyebrow, headline, message, footer);
    placePdpCard();
  };

  const setPdpVisible = (visible) => {
    if (!pdpCard) return;
    pdpCard.setAttribute("aria-hidden", visible ? "false" : "true");
    pdpCard.classList.toggle("nutpmx-pdp-offer--visible", visible);
  };

  const updatePdpOffer = (now, reevaluate = false) => {
    if (!/\/products\//i.test(window.location.pathname)) {
      activePdpCampaignCache = null;
      setPdpVisible(false);
      return;
    }

    if (reevaluate || !activePdpCampaignCache) {
      activePdpCampaignCache = activePdpCampaign(now);
    }

    const campaign = activePdpCampaignCache;
    if (!campaign) {
      setPdpVisible(false);
      return;
    }

    const startsAt = parseTime(campaign.startsAt);
    const endsAt = parseTime(campaign.endsAt);
    if ((startsAt !== null && now < startsAt) || endsAt === null || now >= endsAt) {
      if (endsAt !== null && now >= endsAt) {
        updateAmounts(pdpAmountNodes, pdpLastValues, 0);
      }
      setPdpVisible(false);
      return;
    }

    const nextSignature = JSON.stringify({
      id: campaign.id,
      endsAt: campaign.endsAt,
      offer: campaign.pdpOffer,
    });
    if (!pdpCard || nextSignature !== pdpSignature) {
      pdpSignature = nextSignature;
      buildPdpCard(campaign);
    }

    const countdown = octoberDropCountdown(campaign.id, endsAt, now);
    markCountdownDrop(pdpCard, ".nutpmx-pdp-offer__timer-heading", countdown);
    updateAmounts(pdpAmountNodes, pdpLastValues, countdown.endsAt - now);
    const placed = placePdpCard();
    setPdpVisible(placed);
  };

  const tick = () => {
    if (destroyed) return;
    const now = currentTime();

    if (!settings || settings.enabled !== true) {
      setBarVisible(false);
    } else {
      const startsAt = parseTime(settings.startsAt);
      const endsAt = parseTime(settings.endsAt);

      if (startsAt !== null && now < startsAt) {
        setBarVisible(false);
      } else if (endsAt === null) {
        setBarVisible(false);
      } else {
        const countdown = octoberDropCountdown(settings.campaignId, endsAt, now);
        markCountdownDrop(bar, ".nutpmx-cdb__timer-heading", countdown);
        const remaining = countdown.endsAt - now;
        if (remaining <= 0 && settings.hideOnComplete !== false) {
          updateAmounts(amountNodes, lastValues, 0);
          setBarVisible(false);
        } else {
          updateAmounts(amountNodes, lastValues, remaining);
          setBarVisible(true);
          placeBar();
        }
      }
    }

    updatePdpOffer(now, false);
  };

  const applyPayload = (payload) => {
    const generatedAt = Date.parse(payload?.generatedAt || "");
    if (Number.isFinite(generatedAt)) {
      const measuredOffset = generatedAt - Date.now();
      serverOffsetMs =
        Math.abs(measuredOffset) > 60_000 && Math.abs(measuredOffset) < 86_400_000
          ? measuredOffset
          : 0;
    }

    campaigns = Array.isArray(payload?.campaigns) ? payload.campaigns : [];
    const originalSettings = payload?.countdownBar || null;
    const nextSettings = originalSettings?.campaignId === "cmv4ym1ug001gpg3cgsoab3uy"
      ? {
          ...originalSettings,
          subheadlineEn: "Buy 1, Get 1 FREE. Add 2 eligible products to your cart to unlock the offer.",
          subheadlineAr: "اشتري 1 وخد 1 مجانًا. أضف منتجين مؤهلين للسلة لتفعيل العرض.",
        }
      : originalSettings;
    const signature = JSON.stringify(nextSettings);
    const previousSignature = JSON.stringify(settings);
    settings = nextSettings;
    activePdpCampaignCache = null;

    if (signature !== previousSignature) buildBar();
    updatePdpOffer(currentTime(), true);
    tick();
  };

  const scheduleRefresh = () => {
    if (refreshTimer) window.clearTimeout(refreshTimer);
    if (!destroyed) {
      refreshTimer = window.setTimeout(loadSettings, refreshSeconds * 1000);
    }
  };

  async function loadSettings() {
    if (destroyed) return;
    try {
      const response = await window.fetch(proxyPath, {
        credentials: "same-origin",
        cache: "default",
        headers: { Accept: "application/json" },
      });
      if (response.ok) applyPayload(await response.json());
    } catch (error) {
      console.warn("NUT Promo Manager could not load storefront urgency settings", error);
      tick();
    } finally {
      scheduleRefresh();
    }
  }

  const schedulePdpRecheck = (delay = 120) => {
    if (pdpRecheckTimer) window.clearTimeout(pdpRecheckTimer);
    pdpRecheckTimer = window.setTimeout(() => {
      if (destroyed) return;
      activePdpCampaignCache = null;
      updatePdpOffer(currentTime(), true);
    }, delay);
  };

  const onSectionChange = () => {
    if (visibilityTimer) window.clearTimeout(visibilityTimer);
    visibilityTimer = window.setTimeout(() => {
      placeBar();
      if (!pdpCard?.isConnected) placePdpCard();
      schedulePdpRecheck(120);
      tick();
    }, 80);
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") loadSettings();
  };

  const onProductChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const root = target.closest(".nut-pdp-main, .nut-pdp-section");
    if (!root || target.closest(".hdt-sticky-btn-atc, [data-quick-view], .hdt-quick-view")) return;
    if (target.matches("[name='id'], [name^='option'], [data-option-value]")) {
      schedulePdpRecheck(140);
    }
  };

  const onVariantEvent = (event) => {
    if (event.target instanceof Element) {
      const root = event.target.closest(".nut-pdp-main, .nut-pdp-section");
      if (!root) return;
    }
    const eventVariantId = normalizeVariantId(event?.detail?.variant?.id || event?.detail?.variantId);
    if (eventVariantId) lastKnownPdpVariantId = eventVariantId;
    schedulePdpRecheck(90);
  };

  document.addEventListener("shopify:section:load", onSectionChange);
  document.addEventListener("shopify:section:reorder", onSectionChange);
  document.addEventListener("visibilitychange", onVisibilityChange);
  document.addEventListener("change", onProductChange, true);
  document.addEventListener("variant:change", onVariantEvent);
  document.addEventListener("product:variant-change", onVariantEvent);
  document.addEventListener("shopify:variant:change", onVariantEvent);
  window.addEventListener("popstate", onSectionChange);

  tickTimer = window.setInterval(tick, 1000);

  const destroy = () => {
    destroyed = true;
    if (tickTimer) window.clearInterval(tickTimer);
    if (refreshTimer) window.clearTimeout(refreshTimer);
    if (visibilityTimer) window.clearTimeout(visibilityTimer);
    if (pdpRecheckTimer) window.clearTimeout(pdpRecheckTimer);
    document.removeEventListener("shopify:section:load", onSectionChange);
    document.removeEventListener("shopify:section:reorder", onSectionChange);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    document.removeEventListener("change", onProductChange, true);
    document.removeEventListener("variant:change", onVariantEvent);
    document.removeEventListener("product:variant-change", onVariantEvent);
    document.removeEventListener("shopify:variant:change", onVariantEvent);
    window.removeEventListener("popstate", onSectionChange);
    bar?.remove();
    pdpCard?.remove();
  };

  window.__nutpmxCountdownController = {
    destroy,
    reload: loadSettings,
  };

  loadSettings();
})();

  };
  const tryBoot = () => {
    if (started) return true;
    if (!document.querySelector('[data-nutpmx-countdown-bar]')) return false;
    started = true;
    if (observer) observer.disconnect();
    boot();
    return true;
  };
  if (!tryBoot()) {
    observer = new MutationObserver(tryBoot);
    observer.observe(document.documentElement, {childList: true, subtree: true});
  }
})();
